import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2, FileUp, Loader2, PlayCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError } from "@/lib/api";
import {
  createImportBatch,
  executeImportBatch,
  type ImportDocument,
  type ImportRowInput,
} from "@/lib/api/imports";
import {
  useImportBatch,
  useImportBatches,
  useInvalidateImports,
} from "@/lib/api/imports.queries";
import { parseDianListadoFile, ListadoParseError } from "@/lib/dian-listado";
import { useActiveCompany } from "@/lib/company-context";

export const Route = createFileRoute("/companies/$companyId/importaciones/")({
  component: ImportsPage,
});

function ImportsPage() {
  return (
    <RequireAuth>
      <AppShell>
        <ImportsContent />
      </AppShell>
    </RequireAuth>
  );
}

type ParsedFileState = {
  fileName: string;
  rows: ImportRowInput[];
  skippedRows: number;
};

type ExecuteProgress = {
  running: boolean;
  imported: number;
  failed: number;
  skipped: number;
  remaining: number | null;
};

const IDLE_PROGRESS: ExecuteProgress = {
  running: false,
  imported: 0,
  failed: 0,
  skipped: 0,
  remaining: null,
};

function ImportsContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [parsed, setParsed] = useState<ParsedFileState | null>(null);
  const [parsing, setParsing] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [progress, setProgress] = useState<ExecuteProgress>(IDLE_PROGRESS);

  const batchesQuery = useImportBatches(companyId);
  const batchQuery = useImportBatch(companyId, selectedBatchId);
  const invalidateImports = useInvalidateImports(companyId);

  const batch = batchQuery.data;

  async function onFileChange(file: File | undefined) {
    if (!file) return;
    setParsing(true);
    setParsed(null);
    try {
      const result = await parseDianListadoFile(file);
      setParsed({
        fileName: file.name,
        rows: result.rows,
        skippedRows: result.skippedRows,
      });
      toast.success(t("imports.parse_ok", { count: result.rows.length }));
    } catch (err) {
      toast.error(
        err instanceof ListadoParseError
          ? err.message
          : t("imports.parse_error"),
      );
    } finally {
      setParsing(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function onPlan() {
    if (!parsed) return;
    setPlanning(true);
    try {
      const created = await createImportBatch(companyId, parsed.rows);
      setSelectedBatchId(created.id);
      setProgress(IDLE_PROGRESS);
      setParsed(null);
      await invalidateImports();
      toast.success(
        t("imports.plan_ok", {
          count: created.counts.willImport,
          total: created.counts.totalRows,
        }),
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t("imports.plan_error"));
    } finally {
      setPlanning(false);
    }
  }

  async function onExecute(retryFailed = false) {
    if (!batch) return;
    setProgress({ running: true, imported: 0, failed: 0, skipped: 0, remaining: null });
    try {
      let first = true;
      for (;;) {
        const chunk = await executeImportBatch(companyId, batch.id, {
          retryFailed: retryFailed && first,
        });
        first = false;
        setProgress((prev) => ({
          running: chunk.batchStatus !== "completed",
          imported: prev.imported + chunk.imported,
          failed: prev.failed + chunk.failed,
          skipped: prev.skipped + chunk.skippedDuplicate,
          remaining: chunk.remaining,
        }));
        await batchQuery.refetch();
        if (chunk.batchStatus === "completed") break;
      }
      await invalidateImports();
      toast.success(t("imports.execute_done"));
    } catch (err) {
      setProgress((prev) => ({ ...prev, running: false }));
      toast.error(err instanceof ApiError ? err.message : t("imports.execute_error"));
    }
  }

  const pendingCount = batch
    ? batch.counts.willImport - batch.counts.imported - batch.counts.failed
    : 0;
  const canExecute =
    Boolean(batch) && batch!.status !== "completed" && pendingCount > 0 && !progress.running;
  const failedCount = batch?.counts.failed ?? 0;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t("imports.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("imports.subtitle")}</p>
      </div>

      {/* ── Step 1: upload the listado ─────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileUp className="h-4 w-4" />
            {t("imports.upload_title")}
          </CardTitle>
          <CardDescription>{t("imports.upload_body")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <Input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            disabled={parsing || planning}
            onChange={(e) => void onFileChange(e.target.files?.[0])}
            className="max-w-sm"
          />
          {parsing ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : null}
          {parsed ? (
            <>
              <p className="text-sm text-muted-foreground">
                {t("imports.parsed_summary", {
                  count: parsed.rows.length,
                  file: parsed.fileName,
                })}
                {parsed.skippedRows > 0
                  ? ` · ${t("imports.parsed_skipped", { count: parsed.skippedRows })}`
                  : null}
              </p>
              <Button onClick={() => void onPlan()} disabled={planning}>
                {planning ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                {t("imports.plan_button")}
              </Button>
            </>
          ) : null}
        </CardContent>
      </Card>

      {/* ── Step 2: the plan + execution ───────────────────────────────── */}
      {selectedBatchId ? (
        batchQuery.isPending ? (
          <div className="flex items-center justify-center rounded-lg border bg-card py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : batchQuery.isError ? (
          <ErrorState
            title={t("imports.batch_error_title")}
            description={t("imports.batch_error_body")}
            error={batchQuery.error}
            onRetry={() => void batchQuery.refetch()}
            retryLabel={t("common.retry")}
          />
        ) : batch ? (
          <Card>
            <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
              <div>
                <CardTitle className="text-base">
                  {t("imports.batch_title", {
                    date: new Date(`${batch.createdAt.replace(" ", "T")}Z`).toLocaleString(),
                  })}
                </CardTitle>
                <CardDescription>
                  {batch.status === "completed"
                    ? t("imports.batch_completed", {
                        imported: batch.counts.imported,
                        failed: batch.counts.failed,
                      })
                    : t("imports.batch_plan_summary", {
                        count: batch.counts.willImport,
                        total: batch.counts.totalRows,
                      })}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {progress.running ? (
                  <span className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    {t("imports.executing", {
                      done: progress.imported + progress.failed + progress.skipped,
                      remaining: progress.remaining ?? "…",
                    })}
                  </span>
                ) : null}
                {batch.status === "completed" ? (
                  <>
                    <Badge variant="outline" className="gap-1 text-emerald-600">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {t("imports.status.completed")}
                    </Badge>
                    {failedCount > 0 && !progress.running ? (
                      <Button variant="outline" onClick={() => void onExecute(true)}>
                        <PlayCircle className="mr-2 h-4 w-4" />
                        {t("imports.retry_failed_button", { count: failedCount })}
                      </Button>
                    ) : null}
                  </>
                ) : canExecute ? (
                  <Button onClick={() => void onExecute()}>
                    <PlayCircle className="mr-2 h-4 w-4" />
                    {t("imports.execute_button", { count: pendingCount })}
                  </Button>
                ) : null}
              </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <PlanCounter
                  label={t("imports.counts.will_import")}
                  value={batch.counts.willImport}
                  tone="primary"
                />
                <PlanCounter
                  label={t("imports.counts.duplicate")}
                  value={batch.counts.duplicate}
                />
                <PlanCounter
                  label={t("imports.counts.unsupported")}
                  value={batch.counts.unsupported}
                />
                <PlanCounter label={t("imports.counts.invalid")} value={batch.counts.invalid} />
              </div>
              {batch.status !== "planned" ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <PlanCounter
                    label={t("imports.counts.imported")}
                    value={batch.counts.imported}
                    tone="success"
                  />
                  <PlanCounter
                    label={t("imports.counts.failed")}
                    value={batch.counts.failed}
                    tone={batch.counts.failed > 0 ? "destructive" : undefined}
                  />
                </div>
              ) : null}

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">#</TableHead>
                      <TableHead>{t("imports.col_document")}</TableHead>
                      <TableHead>{t("imports.col_tipo")}</TableHead>
                      <TableHead>{t("imports.col_grupo")}</TableHead>
                      <TableHead>{t("imports.col_status")}</TableHead>
                      <TableHead>{t("imports.col_detail")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batch.documents.map((doc) => (
                      <TableRow key={doc.rowNumber}>
                        <TableCell className="text-xs text-muted-foreground">
                          {doc.rowNumber}
                        </TableCell>
                        <TableCell>
                          <p className="text-sm font-medium">
                            {doc.documentId ??
                              ([doc.prefijo, doc.folio].filter(Boolean).join("") || "—")}
                          </p>
                          <p className="max-w-[260px] truncate font-mono text-[10px] text-muted-foreground">
                            {doc.cufe}
                          </p>
                        </TableCell>
                        <TableCell className="text-xs">{doc.tipo}</TableCell>
                        <TableCell className="text-xs capitalize">{doc.grupo}</TableCell>
                        <TableCell>
                          <DocumentStatusBadge doc={doc} />
                        </TableCell>
                        <TableCell className="max-w-[320px] text-xs text-muted-foreground">
                          {doc.resultReason ?? doc.planReason ?? "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        ) : null
      ) : null}

      {/* ── History ────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("imports.history_title")}</CardTitle>
          <CardDescription>{t("imports.history_body")}</CardDescription>
        </CardHeader>
        <CardContent>
          {batchesQuery.isPending ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : batchesQuery.isError ? (
            <ErrorState
              title={t("imports.history_error")}
              error={batchesQuery.error}
              onRetry={() => void batchesQuery.refetch()}
              retryLabel={t("common.retry")}
            />
          ) : !batchesQuery.data || batchesQuery.data.batches.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {t("imports.history_empty")}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("imports.col_created")}</TableHead>
                  <TableHead>{t("imports.col_status")}</TableHead>
                  <TableHead className="text-right">{t("imports.counts.will_import")}</TableHead>
                  <TableHead className="text-right">{t("imports.counts.imported")}</TableHead>
                  <TableHead className="text-right">{t("imports.counts.failed")}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {batchesQuery.data.batches.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="text-sm">
                      {new Date(`${row.createdAt.replace(" ", "T")}Z`).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant={row.status === "completed" ? "outline" : "secondary"}>
                        {t(`imports.status.${row.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {row.counts.willImport}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {row.counts.imported}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {row.counts.failed}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedBatchId(row.id);
                          setProgress(IDLE_PROGRESS);
                        }}
                      >
                        {t("imports.view_batch")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PlanCounter({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "primary" | "success" | "destructive";
}) {
  return (
    <div className="rounded-lg border bg-card px-4 py-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={`font-mono text-xl font-semibold ${
          tone === "primary"
            ? "text-primary"
            : tone === "success"
              ? "text-emerald-600"
              : tone === "destructive"
                ? "text-destructive"
                : ""
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function DocumentStatusBadge({ doc }: { doc: ImportDocument }) {
  const { t } = useTranslation();
  if (doc.resultStatus === "imported") {
    return (
      <Badge variant="outline" className="text-emerald-600">
        {t("imports.doc_status.imported")}
      </Badge>
    );
  }
  if (doc.resultStatus === "failed") {
    return <Badge variant="destructive">{t("imports.doc_status.failed")}</Badge>;
  }
  if (doc.resultStatus === "skipped_duplicate") {
    return <Badge variant="secondary">{t("imports.doc_status.skipped_duplicate")}</Badge>;
  }
  if (doc.resultStatus === "unsupported" || doc.planStatus === "unsupported") {
    return <Badge variant="secondary">{t("imports.doc_status.unsupported")}</Badge>;
  }
  if (doc.resultStatus === "processing") {
    return (
      <Badge variant="secondary" className="gap-1">
        <Loader2 className="h-3 w-3 animate-spin" />
        {t("imports.doc_status.processing")}
      </Badge>
    );
  }
  if (doc.planStatus === "invalid") {
    return <Badge variant="destructive">{t("imports.doc_status.invalid")}</Badge>;
  }
  if (doc.planStatus === "duplicate") {
    return <Badge variant="secondary">{t("imports.doc_status.duplicate")}</Badge>;
  }
  return <Badge variant="outline">{t("imports.doc_status.will_import")}</Badge>;
}
